import React, { useState, useEffect } from 'react';
import { Table, Button, ButtonGroup, Row, Col, Modal, Form, Alert, Tabs, Tab, Badge } from 'react-bootstrap';
import { jwtDecode } from 'jwt-decode';
import axiosInstance from "../../api/axiosConfig.jsx";

// Constants for role and status to improve readability
const ROLES = {
    BAAS: 'BAAS',
    HORECAMASTERS: 'HORECAMASTERS',
    MEDEWERKER: 'MEDEWERKER',
    FLOORMANAGER: 'FLOORMANAGER',
    HUMANRESOURCE: 'HUMANRESOURCE'
};

const STATUS = {
    APPROVED: 'APPROVED',
    PENDING: 'PENDING',
    REJECTED: 'REJECTED'
};

// Status badge component for reusability
const StatusBadge = ({ status }) => {
    const getStatusStyle = () => {
        switch (status?.toUpperCase()) {
            case STATUS.APPROVED:
                return 'bg-green-100 text-green-800';
            case STATUS.PENDING:
                return 'bg-yellow-100 text-yellow-800';
            case STATUS.REJECTED:
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${getStatusStyle()}`}>
      {status?.charAt(0).toUpperCase() + status?.slice(1).toLowerCase()}
    </span>
    );
};

// Employee request form component
const CorrectionRequestForm = ({ onSubmit, loading }) => {
    const [requestedTime, setRequestedTime] = useState('');
    const [reason, setReason] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit({ requestedTime, reason });
        // Reset form
        setRequestedTime('');
        setReason('');
    };

    return (
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-semibold mb-4">Urencorrectie aanvragen</h2>
            <Form onSubmit={handleSubmit} className="space-y-4">
                <Form.Group>
                    <Form.Label className="block text-sm font-medium">
                        Aangevraagde tijd
                    </Form.Label>
                    <Form.Control
                        type="datetime-local"
                        value={requestedTime}
                        onChange={(e) => setRequestedTime(e.target.value)}
                        required
                        className="w-full rounded-md"
                    />
                </Form.Group>
                <Form.Group>
                    <Form.Label className="block text-sm font-medium">
                        Reden
                    </Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        required
                        className="w-full rounded-md"
                    />
                </Form.Group>
                <Button
                    variant="primary"
                    type="submit"
                    disabled={!requestedTime || !reason || loading}
                    className="w-full sm:w-auto"
                >
                    {loading ? 'Bezig met indienen...' : 'Verzoek indienen'}
                </Button>
            </Form>
        </div>
    );
};

// User requests table component
const UserRequestsTable = ({ requests, loading }) => {
    return (
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-semibold mb-4">
                Ingediende urencorrecties
            </h2>
            {loading ? (
                <div className="py-4 text-center">Laden...</div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Urencorrectie
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Reden
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                            </th>
                        </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {requests.length > 0 ? (
                            requests.map((request) => (
                                <tr key={request.correctionId}>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                                        {new Date(request.requestedTime).toLocaleString()}
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                        {request.correctionReason || request.reason || "Geen reden opgegeven"}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                                        <StatusBadge status={request.status} />
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="3" className="px-4 py-3 text-center text-sm text-gray-500">
                                    Geen urencorrecties gevonden.
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

// Admin correction requests table component
const AdminCorrectionsTable = ({
                                    corrections,
                                    employees,
                                    workedHours,
                                    onApprove,
                                    onReject,
                                    loading,
                                    editingCorrection,
                                    editedTime,
                                    onEditClick,
                                    onSaveEdit,
                                    onCancelEdit,
                                    onEditTimeChange
                                }) => {
    const formatTime = (dateTimeString) => {
        if (!dateTimeString) return 'N/A';
        const date = new Date(dateTimeString);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    };

    // Helper function to find employee name by email
    const getEmployeeName = (email) => {
        const employee = employees.find(emp => emp.email === email);
        return employee ? `${employee.firstName} ${employee.lastName}` : 'Onbekende medewerker';
    };

    // Function to display full reason on click
    const [expandedReason, setExpandedReason] = useState(null);

    const toggleReasonDisplay = (correctionId) => {
        if (expandedReason === correctionId) {
            setExpandedReason(null);
        } else {
            setExpandedReason(correctionId);
        }
    };

    return (
        <div className="bg-white rounded-lg shadow overflow-hidden">
            {loading ? (
                <div className="py-4 text-center">Laden...</div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Medewerker
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Correctiedatum
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Uitkloktijd
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Gevraagde tijd
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Reden
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Acties
                            </th>
                        </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {corrections.length > 0 ? (
                            corrections.map(correction => {
                                // Get employee info from email
                                const employeeName = getEmployeeName(correction.employeeEmail);
                                const employee = employees.find(emp => emp.email === correction.employeeEmail);
                                const correctionDate = new Date(correction.requestedTime).toLocaleDateString();

                                // Get clock out time if available
                                let clockOutTime = 'N/A';
                                if (employee && workedHours[employee.employeeId]?.length > 0) {
                                    clockOutTime = workedHours[employee.employeeId][0].clockOutTime;
                                }

                                // Determine if this correction's reason is expanded
                                const isExpanded = expandedReason === correction.correctionId;

                                // Get the reason from the appropriate field
                                const correctionReason = correction.correctionReason || correction.reason || "Geen reden opgegeven";

                                // Controleer of deze correctie momenteel wordt bewerkt
                                const isEditing = editingCorrection && editingCorrection.correctionId === correction.correctionId;

                                return (
                                    <React.Fragment key={correction.correctionId}>
                                        <tr>
                                            <td className="px-4 py-3 whitespace-nowrap font-medium text-sm">
                                                {employeeName}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                {correctionDate}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                {formatTime(clockOutTime)}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                {isEditing ? (
                                                    <div className="flex items-center">
                                                        <input
                                                            type="time"
                                                            value={editedTime}
                                                            onChange={(e) => onEditTimeChange(e.target.value)}
                                                            className="border rounded px-2 py-1 text-sm"
                                                        />
                                                    </div>
                                                ) : (
                                                    formatTime(correction.requestedTime)
                                                )}
                                            </td>
                                            <td
                                                className={`px-4 py-3 text-sm ${!isEditing ? 'cursor-pointer hover:bg-gray-50' : ''}`}

                                            >
                                                {isExpanded ?
                                                    correctionReason :
                                                    <div className="max-w-xs">
                                                        {correctionReason}
                                                        <span className="text-blue-500 ml-1"></span>
                                                    </div>
                                                }
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                <StatusBadge status={correction.status} />
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                {isEditing ? (
                                                    <div className="flex space-x-2">
                                                        <Button
                                                            variant="success"
                                                            size="sm"
                                                            onClick={onSaveEdit}
                                                            className="text-xs"
                                                        >
                                                            Opslaan
                                                        </Button>
                                                        <Button
                                                            variant="secondary"
                                                            size="sm"
                                                            onClick={onCancelEdit}
                                                            className="text-xs"
                                                        >
                                                            Annuleren
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <div className="flex space-x-2">
                                                        <Button
                                                            variant="primary"
                                                            size="sm"
                                                            onClick={() => onEditClick(correction)}
                                                            className="text-xs"
                                                        >
                                                            Bewerken
                                                        </Button>

                                                        {correction.status === STATUS.PENDING && (
                                                            <>
                                                                <Button
                                                                    variant="success"
                                                                    size="sm"
                                                                    onClick={() => onApprove(correction.correctionId)}
                                                                    className="text-xs"
                                                                >
                                                                    Goedkeuren
                                                                </Button>
                                                                <Button
                                                                    variant="danger"
                                                                    size="sm"
                                                                    onClick={() => onReject(correction.correctionId)}
                                                                    className="text-xs"
                                                                >
                                                                    Afwijzen
                                                                </Button>
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                        {isExpanded && !isEditing && (
                                            <tr className="bg-gray-50">
                                                <td colSpan="7" className="px-6 py-4 text-sm">
                                                    <div className="font-medium">Volledige reden:</div>
                                                    <div className="mt-1 whitespace-pre-wrap">
                                                        {correctionReason}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="7" className="px-4 py-3 text-center text-sm text-gray-500">
                                    Geen urencorrecties gevonden voor deze periode.
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

// Month selector component
const MonthSelector = ({ selectedMonth, selectedYear, onMonthChange, hasEditPermission, handleShow, handleAddEventFromSession }) => {
    return (
        <Row className="mb-3">
            <Col xs={12} className="d-flex justify-content-between align-items-center flex-wrap">
                <div className="d-flex gap-2">
                    <ButtonGroup>
                        <Button variant="outline-secondary" onClick={() => onMonthChange(-1)}>
                            &laquo;
                        </Button>
                        <Button variant="outline-primary">
                            {new Date(selectedYear, selectedMonth).toLocaleString('default', {
                                month: 'long',
                                year: 'numeric'
                            })}
                        </Button>
                        <Button variant="outline-secondary" onClick={() => onMonthChange(1)}>
                            &raquo;
                        </Button>
                    </ButtonGroup>

                    {hasEditPermission && (
                        <>
                            <Button variant="primary" onClick={handleShow}>
                                Nieuwe Dienst
                            </Button>
                            <Button variant="success" onClick={handleAddEventFromSession}>
                                Rooster Bevestigen
                            </Button>
                        </>
                    )}
                </div>
            </Col>
        </Row>
    );
};

// Reject modal component
const RejectModal = ({ show, onHide, onSubmit, loading }) => {
    const [reason, setReason] = useState('');

    const handleSubmit = () => {
        onSubmit(reason);
        setReason('');
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            className="sm:max-w-lg"
            centered
        >
            <Modal.Header closeButton className="px-4 sm:px-6 py-3">
                <Modal.Title className="text-lg font-medium">
                    Verzoek afwijzen
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="px-4 sm:px-6 py-4">
                <Form>
                    <Form.Group>
                        <Form.Label className="block text-sm font-medium mb-2">
                            Reden voor afwijzen
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            className="w-full rounded-md"
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer className="px-4 sm:px-6 py-3 flex justify-end space-x-2">
                <Button
                    variant="secondary"
                    onClick={onHide}
                    className="text-sm"
                    disabled={loading}
                >
                    Annuleren
                </Button>
                <Button
                    variant="danger"
                    onClick={handleSubmit}
                    disabled={!reason.trim() || loading}
                    className="text-sm"
                >
                    {loading ? 'Bezig...' : 'Afwijzen'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

// Main component
const TimeCorrection = () => {
    // User state
    const [userInfo, setUserInfo] = useState({
        role: '',
        companyId: null,
        email: null
    });

    // UI state
    const [activeTab, setActiveTab] = useState('all');
    const [loading, setLoading] = useState({
        employees: false,
        corrections: false,
        userRequests: false,
        submit: false
    });
    const [error, setError] = useState(null);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [selectedCorrectionId, setSelectedCorrectionId] = useState(null);
    const [isSmallScreen, setIsSmallScreen] = useState(false);
    const [editingCorrection, setEditingCorrection] = useState(null);
    const [editedTime, setEditedTime] = useState('');

    // Data state
    const [employees, setEmployees] = useState([]);
    const [corrections, setCorrections] = useState([]);
    const [userRequests, setUserRequests] = useState([]);
    const [workedHours, setWorkedHours] = useState({});

    // Filter state
    const [dateFilter, setDateFilter] = useState({
        month: new Date().getMonth(),
        year: new Date().getFullYear()
    });

    const handleEditClick = (correction) => {
        // Controleer de ontvangen waarde
        console.log("Geklikte correctie:", correction);

        // Zet de juiste tijd in het format dat input[type="time"] verwacht (HH:MM)
        const date = new Date(correction.requestedTime);
        console.log("Parsed Date:", date.toISOString());

        // Gebruik UTC-tijd om tijdzoneproblemen te vermijden
        const hours = date.getUTCHours().toString().padStart(2, '0');
        const minutes = date.getUTCMinutes().toString().padStart(2, '0');

        setEditedTime(`${hours}:${minutes}`);
        setEditingCorrection(correction);
    };

    const handleSaveEdit = async () => {
        setLoading(prev => ({ ...prev, submit: true }));

        try {
            // Haal de originele datum van de correctie op
            const originalDate = new Date(editingCorrection.requestedTime);
            console.log("Originele correctie datum:", originalDate.toISOString());

            // Splits de tijd in uren en minuten
            const [hours, minutes] = editedTime.split(':').map(Number);

            // Maak een nieuwe datum in UTC
            const newDate = new Date(Date.UTC(
                originalDate.getUTCFullYear(),
                originalDate.getUTCMonth(),
                originalDate.getUTCDate(),
                hours,
                minutes
            ));

            // Log de verstuurde gegevens
            console.log("Verstuurde data:", {
                correctionId: editingCorrection.correctionId,
                newRequestedTime: newDate.toISOString(),
                localeTime: newDate.toLocaleString(),
            });

            // Stuur de update naar de server
            await axiosInstance.put(`/corrections/update-time/${editingCorrection.correctionId}`, {
                newRequestedTime: newDate.toISOString(),
            });

            // Vernieuw de gegevens
            fetchCompanyCorrections();
            setEditingCorrection(null);
        } catch (error) {
            console.error('Error updating correction time:', error);
            setError('Er is een probleem met het bijwerken van de correctietijd.');
        } finally {
            setLoading(prev => ({ ...prev, submit: false }));
        }
    };


    const handleCancelEdit = () => {
        setEditingCorrection(null);
        setEditedTime('');
    };

    // Check window size for responsive design - now considers tablets/iPads smaller than 14-inch MacBook
    useEffect(() => {
        const checkScreenSize = () => {
            // 1200px is approximately the width of a 14-inch MacBook
            setIsSmallScreen(window.innerWidth < 1200);
        };

        // Initial check
        checkScreenSize();

        // Set up listener for window resize
        window.addEventListener('resize', checkScreenSize);

        // Clean up
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    // Set default tab to 'all' on small screens (tablets/iPads)
    useEffect(() => {
        if (isSmallScreen) {
            setActiveTab('all');
        }
    }, [isSmallScreen]);

    // Debug logging - remove in production
    useEffect(() => {
        console.log("Current employees:", employees);
    }, [employees]);

    // Decode token and set user info
    useEffect(() => {
        const token = sessionStorage.getItem('authToken');
        if (!token) return;

        try {
            const decoded = jwtDecode(token);
            setUserInfo({
                role: decoded.roles[0],
                companyId: decoded.companyId,
                email: decoded.email
            });
        } catch (error) {
            console.error('Error decoding token:', error);
            setError('Er is een probleem met uw authenticatie. Probeer opnieuw in te loggen.');
        }
    }, []);

    // Load data based on role
    useEffect(() => {
        if (userInfo.email) {
            fetchUserCorrectionRequests();
        }

        if ((userInfo.role === ROLES.BAAS || userInfo.role === ROLES.HORECAMASTERS) && userInfo.companyId) {
            fetchEmployees();
        }
    }, [userInfo]);

    // Fetch company corrections after employees are loaded
    useEffect(() => {
        if ((userInfo.role === ROLES.BAAS || userInfo.role === ROLES.HORECAMASTERS) &&
            userInfo.companyId &&
            employees.length > 0) {
            fetchCompanyCorrections();
        }
    }, [employees, dateFilter, userInfo.companyId, userInfo.role]);

    // Fetch worked hours for employees
    useEffect(() => {
        if (employees.length > 0) {
            employees.forEach(employee => {
                fetchWorkedHours(employee.employeeId);
            });
        }
    }, [employees]);

    // Fetch employee list
    const fetchEmployees = async () => {
        if (!userInfo.companyId) return;

        setLoading(prev => ({ ...prev, employees: true }));
        try {
            const response = await axiosInstance.get(`/employees/company/${userInfo.companyId}`);
            setEmployees(response.data);
            console.log("Fetched employees:", response.data);
        } catch (error) {
            console.error('Error fetching employees:', error);
            setError('Er is een probleem met het ophalen van medewerkers.');
        } finally {
            setLoading(prev => ({ ...prev, employees: false }));
        }
    };

    // Fetch user's correction requests
    const fetchUserCorrectionRequests = async () => {
        if (!userInfo.email) return;

        setLoading(prev => ({ ...prev, userRequests: true }));
        try {
            const response = await axiosInstance.get(`/corrections/all/email/${userInfo.email}`);
            setUserRequests(response.data);
        } catch (error) {
            console.error('Error fetching user correction requests:', error);
            setError('Er is een probleem met het ophalen van uw correctieverzoeken.');
        } finally {
            setLoading(prev => ({ ...prev, userRequests: false }));
        }
    };

    // Fetch company correction requests
    const fetchCompanyCorrections = async () => {
        if (!userInfo.companyId) return;

        setLoading(prev => ({ ...prev, corrections: true }));
        try {
            const response = await axiosInstance.get(`/corrections/all/company/${userInfo.companyId}`);
            console.log("Fetched correction requests:", response.data);

            // Filter corrections by date
            const filtered = response.data.filter(correction => {
                const correctionDate = new Date(correction.requestedTime);
                return (
                    correctionDate.getMonth() === dateFilter.month &&
                    correctionDate.getFullYear() === dateFilter.year
                );
            });

            setCorrections(filtered);
        } catch (error) {
            console.error('Error fetching company corrections:', error);
            setError('Er is een probleem met het ophalen van correctieverzoeken.');
        } finally {
            setLoading(prev => ({ ...prev, corrections: false }));
        }
    };

    // Fetch worked hours for an employee
    const fetchWorkedHours = async (employeeId) => {
        try {
            const response = await axiosInstance.get(`/time/worked-hours/${employeeId}`);
            setWorkedHours(prev => ({ ...prev, [employeeId]: response.data }));
        } catch (error) {
            console.error('Error fetching worked hours:', error);
        }
    };

    // Submit a correction request
    const submitCorrectionRequest = async (formData) => {
        setLoading(prev => ({ ...prev, submit: true }));
        try {
            await axiosInstance.post('/corrections/request', {
                requestedTime: formData.requestedTime,
                reason: formData.reason,
                employeeEmail: userInfo.email,
                companyId: userInfo.companyId
            });

            fetchUserCorrectionRequests();
            setError(null);
        } catch (error) {
            console.error('Error submitting correction request:', error);
            setError('Er is een probleem met het indienen van uw correctieverzoek.');
        } finally {
            setLoading(prev => ({ ...prev, submit: false }));
        }
    };

    // Approve a correction request
    const approveCorrection = async (correctionId) => {
        try {
            await axiosInstance.put(`/corrections/approve/${correctionId}`);
            fetchCompanyCorrections();
        } catch (error) {
            console.error('Error approving correction:', error);
            setError('Er is een probleem met het goedkeuren van het correctieverzoek.');
        }
    };

    // Open reject modal
    const handleRejectClick = (correctionId) => {
        setSelectedCorrectionId(correctionId);
        setShowRejectModal(true);
    };

    // Reject a correction request
    const rejectCorrection = async (reason) => {
        setLoading(prev => ({ ...prev, submit: true }));
        try {
            console.log('Verstuurde reden naar de backend:', reason);
            // Verander de sleutel 'reason' naar 'rejectionReason' om de backend verwachting te matchen
            await axiosInstance.put(`/corrections/reject/${selectedCorrectionId}`, { rejectionReason: reason });
            fetchCompanyCorrections();
            setShowRejectModal(false);
            setSelectedCorrectionId(null);
        } catch (error) {
            console.error('Error rejecting correction:', error);
            setError('Er is een probleem met het afwijzen van het correctieverzoek.');
        } finally {
            setLoading(prev => ({ ...prev, submit: false }));
        }
    };

    // Handle month/year change
    const handleMonthChange = (increment) => {
        const newDate = new Date(dateFilter.year, dateFilter.month + increment, 1);
        setDateFilter({
            month: newDate.getMonth(),
            year: newDate.getFullYear()
        });
    };

    // Filter corrections by status
    const getFilteredCorrections = () => {
        if (activeTab === 'pending') {
            return corrections.filter(c => c.status.toUpperCase() === STATUS.PENDING);
        } else if (activeTab === 'approved') {
            return corrections.filter(c => c.status.toUpperCase() === STATUS.APPROVED);
        } else if (activeTab === 'rejected') {
            return corrections.filter(c => c.status.toUpperCase() === STATUS.REJECTED);
        }
        return corrections;
    };

    // Determine if user is admin
    const isAdmin = userInfo.role === ROLES.BAAS || userInfo.role === ROLES.HORECAMASTERS;
    const isEmployee = userInfo.role === ROLES.MEDEWERKER || userInfo.role === ROLES.FLOORMANAGER || userInfo.role === ROLES.HUMANRESOURCE;

    // Create tabs based on screen size
    const renderTabs = () => {
        // For small screens (including tablets/iPads smaller than 14-inch MacBook), only show "All" tab
        if (isSmallScreen) {
            return (
                <Tabs activeKey="all" className="mb-4">
                    <Tab eventKey="all" title="All" />
                </Tabs>
            );
        }

        // For larger screens, show all tabs
        return (
            <Tabs
                activeKey={activeTab}
                onSelect={setActiveTab}
                className="mb-4"
            >
                <Tab
                    eventKey="pending"
                    title={
                        <span>Pending <Badge bg="warning" className="ml-1">{corrections.filter(c => c.status.toUpperCase() === STATUS.PENDING).length}</Badge></span>
                    }
                />
                <Tab
                    eventKey="approved"
                    title={
                        <span>Approved <Badge bg="success" className="ml-1">{corrections.filter(c => c.status.toUpperCase() === STATUS.APPROVED).length}</Badge></span>
                    }
                />
                <Tab
                    eventKey="rejected"
                    title={
                        <span>Rejected <Badge bg="danger" className="ml-1">{corrections.filter(c => c.status.toUpperCase() === STATUS.REJECTED).length}</Badge></span>
                    }
                />
                <Tab eventKey="all" title="All" />
            </Tabs>
        );
    };

    return (
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary mb-6">
                Uren correcties
            </h1>

            {error && (
                <Alert variant="danger" className="mb-4" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            )}

            {isAdmin && (
                <>
                    <MonthSelector
                        selectedMonth={dateFilter.month}
                        selectedYear={dateFilter.year}
                        onMonthChange={handleMonthChange}
                    />

                    {renderTabs()}

                    <AdminCorrectionsTable
                        corrections={getFilteredCorrections()}
                        employees={employees}
                        workedHours={workedHours}
                        onApprove={approveCorrection}
                        onReject={handleRejectClick}
                        loading={loading.corrections || loading.employees}
                        editingCorrection={editingCorrection}
                        editedTime={editedTime}
                        onEditClick={handleEditClick}
                        onSaveEdit={handleSaveEdit}
                        onCancelEdit={handleCancelEdit}
                        onEditTimeChange={value => setEditedTime(value)}
                    />
                </>
            )}

            {isEmployee && (
                <div className="space-y-6">
                    <CorrectionRequestForm
                        onSubmit={submitCorrectionRequest}
                        loading={loading.submit}
                    />

                    <UserRequestsTable
                        requests={userRequests}
                        loading={loading.userRequests}
                    />
                </div>
            )}

            <RejectModal
                show={showRejectModal}
                onHide={() => setShowRejectModal(false)}
                onSubmit={rejectCorrection}
                loading={loading.submit}
            />
        </div>
    );
};

export default TimeCorrection;
